/**
 * Loading, nothing-here, and something-went-wrong - the three screens a dashboard spends
 * more time showing than anyone admits.
 *
 * THREE RULES, ALL THREE FROM THE OLD VERSION'S FAILURES.
 *
 * A wait is drawn as the SHAPE OF THE ANSWER, not as the word "Loading". A skeleton in
 * the layout the content will occupy means the page does not jump when it arrives, and
 * the reader can already see whether they are getting tiles or a table.
 *
 * An empty state SAYS WHAT TO DO NEXT. "No calls yet" is a dead end; "Nothing matches
 * these filters - test calls are hidden, try the toggle" is a way out. Every empty state
 * here takes a hint or an action for exactly that reason. The action is a LINK OR A
 * HANDLER, because half the ways out of an empty state are not navigation: the calls list
 * gets out of "nothing matches these filters" by clearing the filters, which is a
 * setState on the screen you are already on. When the action could only be a route, that
 * screen had a hint telling the reader to do something and no way to do it.
 *
 * An empty state LOOKS LIKE ITS SUBJECT. Every one of these used to draw the same grey
 * `Info` circle - the glyph that appears nowhere else in the product - so an empty calls
 * table, a call with no transcript and a 404 were typographically the same event. The
 * icon is a prop now, taken as a COMPONENT rather than an element so this file fixes the
 * size: a 26-glyph set drawn to one stroke weight is worth nothing if nine call sites
 * each pick their own `size`.
 */

import type { ComponentType, CSSProperties, ReactNode } from "react";
import { Button, ButtonTo, cx } from "../ui";
import { Alert, Info } from "./Icons";

export function Skeleton({
  height,
  width,
  className,
}: {
  height?: number | string;
  width?: number | string;
  className?: string;
}) {
  const style: CSSProperties = {};
  if (height !== undefined) style.height = height;
  if (width !== undefined) style.width = width;
  return <span className={cx("skeleton", className)} style={style} />;
}

/**
 * The stat strip, the shape the overview opens with.
 *
 * ONE BLOCK, NOT `count` OF THEM. The strip is a single fused card with hairlines inside
 * it, so a row of separate placeholders would be a picture of the layout this page had
 * before - and a skeleton that is the wrong shape is worse than none, because the page
 * jumps exactly as far as the skeleton was wrong. `count` is gone with the tiles.
 */
export function SkeletonTiles() {
  return <Skeleton height={132} className="mb-5 rounded-xl" />;
}

export function SkeletonRows({ rows = 8 }: { rows?: number }) {
  return (
    <div className="grid gap-2">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} height={44} />
      ))}
    </div>
  );
}

/**
 * The way out, as either half of the same shape.
 *
 * A union rather than two optional fields, so a call site physically cannot pass both and
 * leave the reader with a button whose two behaviours disagree.
 */
export type EmptyAction = { label: string; to: string } | { label: string; onClick: () => void };

/** Everything in `Icons.tsx` matches this. The size is set here, not by the caller. */
type Glyph = ComponentType<{ size?: number }>;

export function Empty({
  title,
  body,
  hint,
  action,
  icon,
  tone,
  compact,
}: {
  title: string;
  body?: ReactNode;
  /** The way out. Rendered quieter than the body, because it is instruction not news. */
  hint?: ReactNode;
  action?: EmptyAction;
  /**
   * The glyph for THIS absence - `Phone` for no calls, `Quote` for no transcript. Passed
   * as the component, not as an element; see the header. `tone="alert"` overrides it,
   * because a failure is a failure whatever it was about.
   */
  icon?: Glyph;
  tone?: "alert";
  /** Inside a panel rather than instead of a page. */
  compact?: boolean;
}) {
  const alert = tone === "alert";
  const Mark: Glyph = alert ? Alert : (icon ?? Info);
  /*
   * FILLED AT PAGE SIZE, because there it is the only pressable thing on the screen. A
   * bordered white button, centred on a white bordered card, is a button people look
   * straight past - and an empty state whose one way out goes unseen is the dead end this
   * file exists to prevent. Inside a panel there is other chrome to compete with and the
   * quiet button is right.
   *
   * THIS ARGUMENT GOT STRONGER WHEN THE GROUND WENT GREY, not weaker. The card is still
   * white and the quiet button is still white, so the white-on-white problem the note
   * above describes is now the sharpest it has ever been - and the primary button is ink
   * rather than indigo, which is a bigger step away from the card than the accent was.
   * The two-palettes rule is untouched either way: chrome is neutral, data is the bands.
   */
  const variant = compact ? "default" : "primary";

  return (
    <div
      className={cx(
        "grid justify-items-center gap-2 text-center",
        compact ? "px-4 py-8" : "rounded-lg border border-line bg-paper px-6 py-13",
      )}
    >
      {/*
        A drawn ring, not a filled blob. The product separates everything with a line and
        nothing with a shadow, and a bare tinted circle was the one element on the screen
        that had neither - which is what made it read as a browser warning dropped into
        the page rather than as part of it.
      */}
      <span
        className={cx(
          "mb-0.5 grid place-items-center rounded-full border",
          compact ? "size-9" : "size-11",
          alert ? "border-alert-line bg-alert-soft text-alert" : "border-line bg-sunken text-ink-faint",
        )}
      >
        <Mark size={compact ? 18 : 20} />
      </span>
      {/*
        `text-base` when compact, and that is a hierarchy fix rather than a taste one. A
        `Panel`'s own heading is `text-base font-semibold`; at `text-lg` the words "no
        transcript" were LARGER than the title of the panel they were sitting inside.
      */}
      {/* Capped, so a long title wraps BEFORE it out-runs the prose underneath it. A
          centred heading wider than its own paragraph reads as a banner. */}
      <h3 className={cx("max-w-[30ch] font-semibold", compact ? "text-base" : "text-lg")}>
        {title}
      </h3>
      {/*
        ONE MEASURE, ON ONE BOX. The width used to be set twice - `max-w-[46ch]` on the
        body and again on the hint - and `ch` resolves against each element's OWN font
        size, so the moment the hint stopped being 14px the two boxes were different
        widths, centred, with a ragged right edge that looks like a bug rather than a
        choice. The measure belongs to the column, so it lives on the column.
      */}
      {(body || hint) && (
        <div className="grid max-w-[44ch] gap-1.5 text-sm">
          {body && <p className="text-ink-soft">{body}</p>}
          {/* Quieter by SIZE as well as colour. `ink-soft` to `ink-faint` at one size was
              a single step where this prop's own doc-comment promises two, and four lines
              in two greys read as one paragraph instead of as news then instruction. */}
          {hint && <p className="text-xs text-ink-faint">{hint}</p>}
        </div>
      )}
      {action &&
        ("to" in action ? (
          <ButtonTo className="mt-2" variant={variant} to={action.to}>
            {action.label}
          </ButtonTo>
        ) : (
          <Button className="mt-2" variant={variant} onClick={action.onClick}>
            {action.label}
          </Button>
        ))}
    </div>
  );
}

/**
 * The one line a panel shows when it has nothing to draw.
 *
 * Deliberately not the `Empty` block: inside a figure the surrounding caption has
 * already said what the chart is, and a second heading repeating it turns a quiet
 * absence into an error.
 */
export function Quiet({ children }: { children: ReactNode }) {
  return <p className="py-3.5 text-sm text-ink-faint">{children}</p>;
}

/**
 * The row a table draws instead of no rows at all.
 *
 * A `thead` with four column headings and an empty `tbody` under it is the worst empty
 * state in the product: the reader is shown the shape of an answer and then nothing, with
 * no way to tell an organization that has no keys from a request that failed. One row saying so
 * costs nothing and is the only thing the header's columns are promising.
 *
 * `colSpan` rather than a cell per column, and no bottom rule needed - as the only row it
 * IS `tbody tr:last-child`, which `TABLE_BASE` already strips the border from.
 */
export function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td className="border-b border-line-soft px-4 py-6 text-center text-sm text-ink-faint" colSpan={colSpan}>
        {children}
      </td>
    </tr>
  );
}
